<template>
  <div class="playlist-embed-modal-content">
    <CustomInputValidation
      ref="widgetRef"
      v-model="selectedWidget"
      :class="['widget-select', { 'no-validation-error': !validationErrors.widget || selectedWidget }]"
      :input-type="CustomInputType.Multiselect"
      :validation-object="validationErrors.widget && !selectedWidget ? CustomInputValidator.InputMultiselectRequired : {}"
      :label="t('playlist-edit-select-widget')"
      :asteriks="false"
      :show-error-message="true"
      :disabled="!!embedCode"
      :options="[]"
      :value-prop="'widget_id'"
      :label-key="'widget_name'"
      :async="apiService.widget.getWidgets"
      :async-prop="{
        list_only: true,
        sort_by: 'create_date',
        sort_order: SortOrder.Descending,
        status: 'active'
      }"
      :spread="'widgets'"
      :label-key-with-id="true"
      :track-by="['widget_id', 'widget_name']"
      :delay="-1"
      :close-on-select="true"
      :resolve-on-load="true"
      :can-deselect="false"
      :can-clear="false"
      :clear-on-select="true"
      :clear-on-blur="true"
      :object="false"
      :searchable="true"
      :mode="'single'"
      :tooltip="t('playlist-edit-select-widget-info')"
      :placeholder="t('playlist-edit-select-widget')"
      :custom-error-messages="{ required: t('widget-selected-validation') }"
      @change="selectWidget"
    />
    <div class="playlists-section">
      <TextIconTooltip class="info-label" :text="t('playlists-playlists')" :tooltip="t('playlists-embed-max-two-tooltip')" text-size="0.875rem" />
      <div class="playlist-option">
        <CustomInputValidation
          ref="secondPlaylistRef"
          v-model="firstPlaylistRef"
          class="playlist-group"
          :input-type="CustomInputType.Multiselect"
          :disabled="true"
          :asteriks="false"
          :show-error-message="false"
        />
        <div class="orientation-section">
          <div class="orientation-options">
            <OptionWrapper
              as="div"
              :border-radius="'4px'"
              :selected="selectedOrientation === Orientation.Horizontal"
              :has-error="showNoOrientationWarning"
              :disabled="Boolean(embedCode)"
              @click="selectOrientation(Orientation.Horizontal)"
            >
              <VideoLayoutOrientation :is-vertical="false" :tag-colors="TagColors.White" :size="TagSizes.Big" />
            </OptionWrapper>
            <OptionWrapper
              as="div"
              :border-radius="'4px'"
              :selected="selectedOrientation === Orientation.Vertical"
              :has-error="showNoOrientationWarning"
              :disabled="Boolean(embedCode)"
              @click="selectOrientation(Orientation.Vertical)"
            >
              <VideoLayoutOrientation :is-vertical="true" :tag-colors="TagColors.White" :size="TagSizes.Big" />
            </OptionWrapper>
          </div>
          <ActionButtons
            v-if="!Boolean(embedCode)"
            :actions="deleteFirstPlaylistActions"
            is-visible
            :is-disabled-with-tooltip="t('current-playlist-readonly-tooltip')"
            :is-dots-menu="false"
          />
        </div>
      </div>
      <div v-if="firstPlaylistOptionErrorMessage" class="no-playlist-warning-text">
        {{ firstPlaylistOptionErrorMessage }}
      </div>
      <WithIconButton
        v-if="!showSecondPlaylistSection" class="add-another-playlist-button" :variant="ButtonVariant.TERTIARY" icon-name="addBlueIcon"
        @click="addAnotherPlaylist"
      >
        {{ t('add-another-playlist') }}
      </WithIconButton>
      <div v-if="showSecondPlaylistSection" class="playlist-option second">
        <CustomInputValidation
          ref="secondPlaylistRef"
          v-model="secondPlaylist"
          class="playlist-group"
          :input-type="CustomInputType.Multiselect"
          :validation-object="validationErrors.secondPlaylist && showSecondPlaylistSection && !secondPlaylist ? CustomInputValidator.InputMultiselectRequired : {}"
          :asteriks="false"
          :show-error-message="false"
          :disabled="isInputsDisabled"
          :options="[]"
          :value-prop="'playlist_id'"
          :label-key="'playlist_name'"
          :async="apiService.playlist.getPlaylists"
          :async-prop="{
            organization_id: store.state.user.user.organization_id,
            sort_by: 'playlist_name',
            sort_order: SortOrder.Ascending,
            list_only: true
          }"
          :spread="'playlists'"
          :label-key-with-id="true"
          :track-by="['playlist_id', 'playlist_name']"
          :delay="-1"
          :close-on-select="true"
          :resolve-on-load="true"
          :can-deselect="false"
          :clear-on-select="true"
          :clear-on-blur="true"
          :object="false"
          :searchable="true"
          :mode="'single'"
          :placeholder="t('select-playlist')"
          @change="selectSecondPlaylist"
        />
        <div class="orientation-section second">
          <div class="orientation-options">
            <OptionWrapper
              as="div"
              :border-radius="'4px'"
              :selected="secondOrientation === Orientation.Horizontal"
              :has-error="showSecondOrientationMissing"
              :disabled="selectedOrientation === Orientation.Horizontal || Boolean(embedCode)"
              @click="selectSecondOrientation(Orientation.Horizontal)"
            >
              <VideoLayoutOrientation :is-vertical="false" :tag-colors="TagColors.White" :size="TagSizes.Big" />
            </OptionWrapper>
            <OptionWrapper
              as="div"
              :border-radius="'4px'"
              :selected="secondOrientation === Orientation.Vertical"
              :has-error="showSecondOrientationMissing"
              :disabled="selectedOrientation === Orientation.Vertical || Boolean(embedCode)"
              @click="selectSecondOrientation(Orientation.Vertical)"
            >
              <VideoLayoutOrientation :is-vertical="true" :tag-colors="TagColors.White" :size="TagSizes.Big" />
            </OptionWrapper>
          </div>
          <ActionButtons v-if="!Boolean(embedCode)" :actions="deleteSecondPlaylistActions" is-visible :is-dots-menu="false" />
        </div>
      </div>
      <div v-if="secondPlaylistOptionErrorMessage" class="no-playlist-warning-text">
        {{ secondPlaylistOptionErrorMessage }}
      </div>
    </div>
    <div class="code-section">
      <TextIconTooltip class="info-label" text-size="0.875rem" :text="t('widgets-get-code-and-embed-code-title')" :tooltip="t('embed-widget-code-info')" />
      <div class="code-container" :class="{ disabled: !embedCode }">
        <pre>{{ embedCode || embedCodePlaceholder }}</pre>
        <ActionButtons class="copy-button" :actions="copyActions" :is-visible="Boolean(embedCode)" :is-dots-menu="false" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { apiService } from '@/services'
import { CustomInputType, SortOrder, CustomModalProps, Orientation } from '@/interfaces'
import { getPlayerCDNDomain } from '@/services/utils'
import * as CustomInputValidator from '@/data/models/inputOptions/input'
import CustomInputValidation from '@/components/common/custom-inputs/CustomInputValidation.vue'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import WithIconButton from '@/components/common/buttons/WithIconButton.vue'
import { ButtonVariant } from '@/styles/types'
import useVuelidate from '@vuelidate/core'
import TextIconTooltip from '@/components/common/tooltips/TextIconTooltip.vue'
import VideoLayoutOrientation from '@/components/base/video-component-tags/VideoLayoutOrientation.vue'
import { TagColors, TagSizes } from '@/components/common/tag/utils'
import { OptionWrapper } from '@/components/common'

interface PlaylistEmbedExtraProps {
  playlistId: string | number
  playlistName: string
}

interface PlaylistEmbedModalProps extends CustomModalProps {
  extraProps?: PlaylistEmbedExtraProps
}

const props = defineProps<PlaylistEmbedModalProps>()
const store = useStore()
const { t } = useI18n()
const selectedWidget = ref<string | number | null>(null)
const secondPlaylist = ref<string | number | null>(null)
const selectedOrientation = ref<Orientation | null>(null)
const selectedSecondOrientation = ref<Orientation | null>(null)
const secondOrientation = ref<Orientation | null>(null)
const embedCode = ref('')
const embedCodePlaceholder = t('embed-select-widget-placeholder')
const canCopy = ref(true)
const showNoWidgetWarning = ref(false)
const showNoOrientationWarning = ref(false)
const showSecondPlaylistMissing = ref(false)
const showSecondOrientationMissing = ref(false)
const showSecondPlaylistSection = ref(false)
const isEditing = ref(false)
const validationErrors = ref({
  widget: false,
  orientation: false,
  secondPlaylist: false,
  secondOrientation: false
})

const clearValidationErrors = () => {
  Object.keys(validationErrors.value).forEach((key) => {
    validationErrors.value[key as keyof typeof validationErrors.value] = false
  })
}

const checkSecondPlaylistValidations = () => {
  const hasSecondPlaylist = Boolean(secondPlaylist.value)
  const hasSecondOrientation = Boolean(secondOrientation.value)
  const playlistValidationTriggered = validationErrors.value.secondPlaylist
  const orientationValidationTriggered = validationErrors.value.secondOrientation
  showSecondPlaylistMissing.value = playlistValidationTriggered && !hasSecondPlaylist
  if (hasSecondPlaylist && !hasSecondOrientation) {
    validationErrors.value.secondOrientation = true
  }
  showSecondOrientationMissing.value = orientationValidationTriggered && hasSecondPlaylist && !hasSecondOrientation
}

const widgetRef = ref()
const firstPlaylistRef = ref([props.extraProps?.playlistName])
const secondPlaylistRef = ref()
const isInputsDisabled = computed(() => Boolean(embedCode.value))
const firstPlaylistOptionErrorMessage = computed(() => {
  if (!showNoOrientationWarning.value) return ''
  return t('orientation-validation')
})
const secondPlaylistOptionErrorMessage = computed(() => {
  if (showSecondPlaylistMissing.value && showSecondOrientationMissing.value) {
    return t('playlist-orientation-validation')
  }
  if (showSecondOrientationMissing.value) {
    return t('orientation-validation')
  }
  if (showSecondPlaylistMissing.value) {
    return t('playlist-selected-validation')
  }
  return ''
})

onMounted(() => {
  store.dispatch('modal/setModalPayload', { getCode: handleGetCodeClick, startEditing })
  store.dispatch('modal/setExtraProps', { embedCode: embedCode })
})

const rules = {
  selectedWidget: {
    requiredWidget: (value: string | number | null) => Boolean(value)
  },
  selectedOrientation: {
    requiredOrientation: (value: Orientation | null) => value === Orientation.Horizontal || value === Orientation.Vertical
  },
  secondPlaylist: {
    conditionalSecondPlaylist: (value: string | number | null) => !showSecondPlaylistSection.value || Boolean(value)
  },
  secondOrientation: {
    conditionalSecondOrientation: (value: Orientation | null) => !secondPlaylist.value || value === Orientation.Horizontal || value === Orientation.Vertical
  }
}

const v$ = useVuelidate(rules, {
  selectedWidget,
  selectedOrientation,
  secondPlaylist,
  selectedSecondOrientation,
  secondOrientation
})

const copyActions = [
  {
    label: 'copy',
    handler: copyToClipboard,
    translationKey: 'copy'
  }
]

const deleteFirstPlaylistActions = [
  {
    label: 'delete',
    handler: () => {},
    translationKey: 'delete',
    isDisabledWithTooltip: t('current-playlist-readonly-tooltip')
  }
]

const deleteSecondPlaylistActions = [
  {
    label: 'delete',
    handler: deleteSecondPlaylist,
    translationKey: 'delete'
  }
]

function deleteSecondPlaylist() {
  secondPlaylist.value = null
  secondOrientation.value = null
  showSecondPlaylistSection.value = false
  validationErrors.value.secondPlaylist = false
  validationErrors.value.secondOrientation = false
  showSecondPlaylistMissing.value = false
  showSecondOrientationMissing.value = false
  secondPlaylistRef.value.$reset()
}

function addAnotherPlaylist() {
  showSecondPlaylistSection.value = true
  showSecondPlaylistMissing.value = false
  showSecondOrientationMissing.value = false
  if (selectedOrientation.value) {
    const oppositeOrientation = selectedOrientation.value === Orientation.Horizontal ? Orientation.Vertical : Orientation.Horizontal
    secondOrientation.value = oppositeOrientation
    selectedSecondOrientation.value = oppositeOrientation
  }
}

function copyToClipboard() {
  if (!embedCode.value) return
  navigator.clipboard.writeText(embedCode.value)
  if (canCopy.value) {
    canCopy.value = false
    store.dispatch('messages/addMessage', {
      message: t('library-embed-copy-clipboard'),
      type: 'success'
    })
    setTimeout(() => {
      canCopy.value = true
    }, 2500)
  }
}

function selectWidget() {
  validationErrors.value.widget = false
  v$.value.selectedWidget.$reset()
}

function selectOrientation(orientation: Orientation) {
  selectedOrientation.value = orientation
  showNoOrientationWarning.value = false
  validationErrors.value.orientation = false
  v$.value.selectedOrientation.$reset()
  if (showSecondPlaylistSection.value) {
    const oppositeOrientation = orientation === Orientation.Horizontal ? Orientation.Vertical : Orientation.Horizontal
    secondOrientation.value = oppositeOrientation
    selectedSecondOrientation.value = oppositeOrientation
    showSecondPlaylistMissing.value = false
    showSecondOrientationMissing.value = false
  }
}

function selectSecondPlaylist() {
  validationErrors.value.secondPlaylist = false
  v$.value.secondPlaylist.$reset()
  checkSecondPlaylistValidations()
}

function selectSecondOrientation(orientation: Orientation) {
  selectedSecondOrientation.value = orientation
  secondOrientation.value = orientation
  validationErrors.value.secondOrientation = false
  v$.value.secondOrientation.$reset()
  checkSecondPlaylistValidations()
  const oppositeOrientation = orientation === Orientation.Horizontal ? Orientation.Vertical : Orientation.Horizontal
  selectedOrientation.value = oppositeOrientation
  showNoOrientationWarning.value = false
}

function handleGetCodeClick() {
  generateEmbedCode()
}

async function generateEmbedCode() {
  const playlistId = props.extraProps?.playlistId
  if (!playlistId) {
    return
  }
  validationErrors.value.widget = !selectedWidget.value
  validationErrors.value.orientation = !selectedOrientation.value
  validationErrors.value.secondPlaylist = showSecondPlaylistSection.value && !secondPlaylist.value
  validationErrors.value.secondOrientation = showSecondPlaylistSection.value && !!secondPlaylist.value && !secondOrientation.value

  await nextTick()
  let hasError = false
  if (widgetRef.value) {
    widgetRef.value.validate()
  }
  if (!selectedOrientation.value) {
    showNoOrientationWarning.value = true
    hasError = true
  }
  if (secondPlaylistRef.value && showSecondPlaylistSection.value) {
    secondPlaylistRef.value.validate()
  }

  if (showSecondPlaylistSection.value) {
    const hasSecondPlaylist = Boolean(secondPlaylist.value)
    const hasSecondOrientation = Boolean(secondOrientation.value)

    if (!hasSecondPlaylist && !hasSecondOrientation) {
      showSecondPlaylistMissing.value = true
      showSecondOrientationMissing.value = true
      hasError = true
    } else if (hasSecondPlaylist && !hasSecondOrientation) {
      showSecondOrientationMissing.value = true
      hasError = true
    } else if (!hasSecondPlaylist && hasSecondOrientation) {
      showSecondPlaylistMissing.value = true
      hasError = true
    }
  }

  const hasWidget = Boolean(selectedWidget.value)
  const hasOrientation = Boolean(selectedOrientation.value)
  if (!hasWidget || !hasOrientation) {
    showNoWidgetWarning.value = true
    hasError = true
  }
  if (hasError) {
    embedCode.value = ''
    return
  }

  showNoWidgetWarning.value = false
  showSecondPlaylistMissing.value = false
  showSecondOrientationMissing.value = false
  clearValidationErrors()
  isEditing.value = false

  const baseUrl = getPlayerCDNDomain()
  const scriptStart = '<script data-cfasync="false" async type="text/javascript" src="'
  const scriptEnd = '"></' + 'script>'
  const qs = new URLSearchParams()

  if (selectedOrientation.value === Orientation.Vertical) {
    qs.append('vpid', String(playlistId))
  } else if (selectedOrientation.value === Orientation.Horizontal) {
    qs.append('pid', String(playlistId))
  }

  if (secondPlaylist.value && secondOrientation.value) {
    if (secondOrientation.value === Orientation.Vertical) {
      qs.append('vpid', String(secondPlaylist.value))
    } else if (secondOrientation.value === Orientation.Horizontal) {
      qs.append('pid', String(secondPlaylist.value))
    }
  }

  const url = `${baseUrl}/init/${String(selectedWidget.value)}.js?${qs.toString()}`
  embedCode.value = `${scriptStart}${url}${scriptEnd}`
}

const startEditing = () => {
  isEditing.value = true
  embedCode.value = ''
  clearValidationErrors()
  showNoWidgetWarning.value = false
  showNoOrientationWarning.value = false
  showSecondPlaylistMissing.value = false
  showSecondOrientationMissing.value = false
  v$.value.$reset()
}
</script>

<style lang="scss" scoped>
.playlist-embed-modal-content {
  width: 640px;
  margin-inline: 2px;
  margin-bottom: 1rem;
  gap: 1.5rem;
  display: flex;
  flex-direction: column;

  .widget-select {
    width: 100%;

    &.no-validation-error {
      &.custom-input {
        margin-bottom: 0 !important;
      }
    }
  }

  .playlists-section {
    display: flex;
    flex-direction: column;

    .add-another-playlist-button {
      margin-top: 0.25rem;
    }

    .playlist-option {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 1rem;

      &.second {
        margin-top: 0.75rem;
      }
    }

    .playlist-group.custom-input {
      margin-bottom: 0;
    }

    .playlist-group {
      width: 100%;
    }

    .orientation-options {
      display: flex;
      flex-direction: row;
      gap: 0.75rem;
    }

    .orientation-section {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      flex-direction: row;
      align-items: center;
      gap: 8px;

      .orientation-options {
        flex: 0 0 auto;
        min-width: fit-content;
      }
      :deep(.actions-container) {
        padding: 0;
        width: fit-content;
        flex: 0 0 auto;
      }
    }
  }

  .code-section {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .code-container {
      display: flex;
      border: 1px solid $embedWidgetBorderColor;
      border-radius: 4px;
      overflow: hidden;
      align-items: flex-start;
      padding: 16px 8px 16px 16px;

      pre {
        width: 100%;
        margin: 0;
        min-height: 48px;
        font-size: 12px;
        color: $embedWidgetCodeColor;
        line-height: 1.4;
        white-space: pre-wrap;
        word-break: break-all;
      }

      .copy-button {
        display: flex;
        align-items: flex-start;
        padding: 0.5rem;
        width: fit-content;

        :deep(.actions-container) {
          padding: 0;
        }
      }
    }
  }

  .no-playlist-warning-text {
    color: $error-custom-validation;
    font-size: 0.875rem;
    font-weight: 400;
    margin-top: 0.25rem;
  }
  .info-label {
    color: $bodyText;
  }
}
</style>
